import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, IRowNode } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { AsyncConfirmationDialogComponent } from '../../../../core/components/async-confirmation-dialog/async-confirmation-dialog.component';
import { LinkButtonComponent } from '../../../../core/components/buttons/link/link-button.component';
import { LoadingButtonComponent } from '../../../../core/components/buttons/loading/loading-button.component';
import { FormErrorsComponent } from '../../../../core/components/form-errors/form-errors.component';
import { InputFechaComponent } from '../../../../core/components/inputs/input-fecha/input-fecha.component';
import { InputNumberComponent } from '../../../../core/components/inputs/input-number/input-number.component';
import { InputTextareaComponent } from '../../../../core/components/inputs/input-textarea/input-textarea.component';
import { AG_GRID_DEFAULT_OPTIONS, removeRowButton } from '../../../../core/config/aggrid.default.config';
import { BackendErrorService } from '../../../../core/services/backend-error/backend-error.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { ReplaceEmptyStringsWithNull } from '../../../../core/utils/ReplaceEmptyStringsWithNull';
import { CuentaBancariaService } from '../../../../maestros/services/maestros/cuenta-bancaria/cuenta-bancaria.service';
import { EmpresaFacturadoraService } from '../../../../maestros/services/maestros/empresa-facturadora/empresa-facturadora.service';
import { CuentaBancaria } from '../../../../models/maestros/cuenta-bancaria.model';
import { EmpresaFacturadora } from '../../../../models/maestros/empresa-facturadora.model';
import { SepaDtoIn, SepaDtoOut, SepaForm } from '../../../models/sepa.model';
import { ContabilidadSepaService } from '../../../services/contabilidad-sepa.service';
import { ContabilidadVentasService } from '../../../services/contabilidad-venta.service';

@Component({
  selector: 'app-sepa-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    AgGridAngular,
    InputFechaComponent,
    InputNumberComponent,
    LoadingButtonComponent,
    FormErrorsComponent,
    AsyncConfirmationDialogComponent,
    InputTextareaComponent,
  ],
  templateUrl: './sepa-detail.component.html',
  styleUrl: './sepa-detail.component.scss',
})
export class SepaDetailComponent implements OnInit {
  sepaId!: number;
  sepaForm!: FormGroup<SepaForm>;
  isEditMode = false;
  isLoading = false;
  sepaData!: SepaDtoOut;
  gridApi!: GridApi;

  mostrarDialogoValidacion = false;
  isLoadingValidar = false;

  mostrarDialogoValidacionContabilidad = false;
  isLoadingValidarContabilidad = false;

  mostrarDiaologoEliminar = false;
  isLoadingEliminar = false;

  mostrarDiaologoGenerarVenta = false;
  isLoadingGenerarVenta = false;

  nuevoCodigoAlbaran: number = 0;
  isLoadingCambiarCodigoAlbaran = false;

  isRemesaDropdownOpen = false;
  isContabilidadDropdownOpen = false;

  empresasRefacturadoras: EmpresaFacturadora[] = [];
  cuentasBancarias: CuentaBancaria[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly sepaService: ContabilidadSepaService,
    private readonly ventaService: ContabilidadVentasService,
    private readonly toastService: ToastService,
    private readonly backendErrorService: BackendErrorService,
    private readonly router: Router,
    private readonly empresaFacturadoraService: EmpresaFacturadoraService,
    private readonly cuentaBancariaService: CuentaBancariaService
  ) {}

  gridOptions: GridOptions = {
    ...AG_GRID_DEFAULT_OPTIONS,
    onGridReady: (params) => (this.gridApi = params.api),
  };

  get columnDefs(): ColDef[] {
    const baseColumns: ColDef[] = [
      { field: 'id', headerName: 'ID' },
      { field: 'codFactura', headerName: 'CodFactura' },
      { field: 'codContable', headerName: 'Codigo Contable' },
      { field: 'clienteNombre', headerName: 'Cliente' },
      { field: 'suministroDireccion', headerName: 'Suministro' },
      { field: 'cuentaBancaria', headerName: 'Cuenta Bancaria' },
      { field: 'baseImponible', headerName: 'Base Imponible' },
      { field: 'importeTotal', headerName: 'Importe Total' },
      {
        field: '',
        headerName: 'Ver Factura',
        cellRenderer: LinkButtonComponent,
        cellRendererParams(params: any) {
          return {
            link: `/facturas-suministros/${params.data.id}`,
          };
        },
        resizable: false,
        sortable: true,
        filter: false,
        suppressMovable: true,
      },
    ];
    if (this.isEditMode) {
      return [...baseColumns, removeRowButton(this.afterRemoveRow.bind(this))];
    }
    return baseColumns;
  }

  afterRemoveRow(rowNode: IRowNode): void {
    const idAEliminar = Number(rowNode.id);
    this.sepaForm
      .get('facturas')
      ?.setValue(this.sepaForm.get('facturas')!.value.filter((id: number) => id !== idAEliminar));
  }

  ngOnInit(): void {
    this.sepaId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadSepa();
  }

  private loadSepa(): void {
    this.sepaService.getSepaById(this.sepaId).subscribe({
      next: (data: SepaDtoOut) => {
        this.cargarTablasMaestras(data.empresaFacturadoraId);

        this.sepaData = data;
        this.initForm(data);
      },
    });
  }

  cargarTablasMaestras(empresaFacturadoraId: number): void {
    this.empresaFacturadoraService.setComboEmpresasFacturadoras(this.empresasRefacturadoras);
    this.cuentaBancariaService.setComboCuentasBancariasByEmpresaRefacturadoraAsync(
      this.cuentasBancarias,
      empresaFacturadoraId
    );
  }

  private initForm(data: SepaDtoOut): void {
    this.sepaForm = this.fb.group<SepaForm>({
      empresaFacturadoraId: this.fb.control(data.empresaFacturadoraId, {
        validators: Validators.required,
        nonNullable: true,
      }),
      cuentaBancariaId: this.fb.control(data.cuentaBancariaId, { validators: Validators.required, nonNullable: true }),
      fechaGeneracionSepa: this.fb.control(data.fechaGeneracionSepa),
      fechaEmision: this.fb.control(data.fechaEmision, { validators: Validators.required, nonNullable: true }),
      fechaCargo: this.fb.control(data.fechaCargo, { validators: Validators.required, nonNullable: true }),
      fechaValidacion: this.fb.control(data.fechaValidacion ?? null),
      codAlbaran: this.fb.control(data.codAlbaran ?? null),
      observaciones: this.fb.control(data.observaciones ?? null),
      facturas: this.fb.control(
        data.facturas.map((f) => f.id),
        { nonNullable: true }
      ),
    });

    this.sepaForm.disable();
  }

  toggleEdit(): void {
    this.isEditMode = !this.isEditMode;
    this.isEditMode ? this.sepaForm.enable() : this.sepaForm.disable();

    this.sepaForm.get('empresaFacturadoraId')?.disable();
    this.sepaForm.get('fechaGeneracionSepa')?.disable();
    this.sepaForm.get('fechaValidacion')?.disable();
  }

  guardarCambios(): void {
    const raw = this.sepaForm.getRawValue();
    const cleaned: SepaDtoIn = ReplaceEmptyStringsWithNull.clean(raw);

    this.isLoading = true;
    this.sepaService.actualizarSepa(cleaned, this.sepaId).subscribe({
      next: () => {
        this.toastService.success('SEPA actualizado');
        this.isEditMode = false;
        this.sepaForm.disable();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.error?.errors) {
          this.backendErrorService.handleBackendErrors(err.error.errors, this.sepaForm);
        } else {
          this.toastService.error('Error al actualizar el SEPA');
        }
      },
    });
  }

  exportarRemesaExcel(): void {
    this.sepaService.exportarRemesaSepaExcel(this.sepaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sepa_remesa_${this.sepaData.id}_${this.sepaData.fechaGeneracionSepa}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar la remesa sepa');
      },
    });
  }

  exportarRemesaCsv(): void {
    this.sepaService.exportarRemesaSepaCsv(this.sepaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sepa_remesa_${this.sepaData.id}_${this.sepaData.fechaGeneracionSepa}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar la remesa sepa');
      },
    });
  }

  exportarContabilidadExcel(): void {
    this.sepaService.exportarContabilidadSepaExcel(this.sepaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sepa_contabilidad_${this.sepaData.id}_${this.sepaData.fechaGeneracionSepa}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar la contabilidad sepa');
      },
    });
  }

  exportarContabilidadCsv(): void {
    this.sepaService.exportarContabilidadSepaCsv(this.sepaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sepa_contabilidad_${this.sepaData.id}_${this.sepaData.fechaGeneracionSepa}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar la contabilidad sepa');
      },
    });
  }

  cancelarEdicion(): void {
    this.initForm(this.sepaData);
    this.isEditMode = false;
  }

  validarRemesaSepa(): void {
    this.isLoadingValidar = true;
    this.sepaService
      .validarRemesaSepa(this.sepaId)
      .pipe(
        finalize(() => {
          this.isLoadingValidar = false;
          this.mostrarDialogoValidacion = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Remesa SEPA validada correctamente');
          this.loadSepa();
        },
        error: (err) => {
          this.toastService.error('Error al validar la remesea SEPA');
          console.error('Error al validar la remesa SEPA:', err);
        },
      });
  }

  validarContabilidadSepa(): void {
    this.isLoadingValidarContabilidad = true;
    this.sepaService
      .validarContabilidadSepa(this.sepaId)
      .pipe(
        finalize(() => {
          this.isLoadingValidarContabilidad = false;
          this.mostrarDialogoValidacionContabilidad = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Contabilidad SEPA validada correctamente');
          this.loadSepa();
        },
        error: (err) => {
          this.toastService.error('Error al validar la contabilidad SEPA');
          console.error('Error al validar la contabilidad SEPA:', err);
        },
      });
  }

  eliminarSepa(): void {
    this.isLoadingEliminar = true;
    this.sepaService
      .eliminarSepa(this.sepaId)
      .pipe(
        finalize(() => {
          this.isLoadingEliminar = false;
          this.mostrarDiaologoEliminar = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('SEPA eliminado correctamente');
          this.router.navigate(['/contabilidad/sepa']);
        },
        error: (err) => {
          this.toastService.error('Error al eliminar el SEPA');
          console.error('Error al eliminar el SEPA:', err);
        },
      });
  }

  generarVenta(): void {
    this.isLoadingGenerarVenta = true;

    this.ventaService.crearVentaDomiciliacionBancaria(this.sepaId).subscribe({
      next: (data) => {
        this.toastService.success('Venta generada correctamente');
        this.isLoadingGenerarVenta = false;
        this.mostrarDiaologoGenerarVenta = false;
        this.sepaData.ventaId = data.id;
        this.goToVenta();
      },
      error: (err) => {
        this.toastService.error('Error al generar la venta');
        console.error('Error al generar la venta:', err);
        this.isLoadingGenerarVenta = false;
        this.mostrarDiaologoGenerarVenta = false;
      },
    });
  }

  goToVenta() {
    if (!this.sepaData.ventaId) {
      this.toastService.error('No se pudo mostrar la venta');
      return;
    }

    this.router.navigate(['/contabilidad/ventas', this.sepaData.ventaId]);
  }

  cambiarCodigoAlbaran(): void {
    this.isLoadingCambiarCodigoAlbaran = true;

    this.sepaService
      .cambiarCodigoAlbaran(this.sepaId, this.nuevoCodigoAlbaran)
      .pipe(
        finalize(() => {
          this.isLoadingCambiarCodigoAlbaran = false;
          this.cerrarModal('modalCambiarCodigoAlbaran');
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Código de albarán cambiado correctamente');
          this.loadSepa();
        },
        error: (err) => {
          this.toastService.error('Error al cambiar el código de albarán');
          console.error('Error al cambiar el código de albarán:', err);
        },
      });
  }

  abrirModal(modalSeleccionado: string) {
    const modal = document.getElementById(modalSeleccionado) as HTMLDialogElement;
    if (modal) {
      modal.showModal();
    }
  }

  cerrarModal(modalSeleccionado: string) {
    const modal = document.getElementById(modalSeleccionado) as HTMLDialogElement;
    if (modal) {
      modal.close();
    }
  }

  toggleRemesaDropdown(): void {
    this.isRemesaDropdownOpen = !this.isRemesaDropdownOpen;
  }

  toggleContabilidadDropdown(): void {
    this.isContabilidadDropdownOpen = !this.isContabilidadDropdownOpen;
  }
}
